import { CookieConsent } from "@/components/legal/CookieConsent";
import { RouteAccessibility } from "@/components/app/RouteAccessibility";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppLayout } from "@/components/app/AppLayout";
import { RouteFallback } from "@/components/app/RouteFallback";
import { AuthProvider, ProtectedRoute, RoleGuard } from "@/lib/auth";
import { ImpersonationProvider } from "@/lib/auth/ImpersonationContext";
import { AonPrivacyGate } from "@/components/auth/AonPrivacyGate";
import { BrandingProvider, BrandingSync } from "@/lib/branding";

// =============================================================================
// Code-splitting : chaque page est chargée à la demande (chunks séparés).
// Le bundle initial ne contient que le shell (Layout, Auth, Branding).
// =============================================================================

// Public
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const AuthCallbackPage = lazy(() => import("@/pages/auth/AuthCallbackPage"));
const AuthConfirmPage = lazy(() => import("@/pages/auth/AuthConfirmPage"));
const MfaChallengePage = lazy(() => import("@/pages/auth/MfaChallengePage"));
const MfaEnrollPage = lazy(() => import("@/pages/auth/MfaEnrollPage"));
const MentionsLegalesPage = lazy(() => import("@/pages/legal/MentionsLegalesPage"));
const AccessibilityPage = lazy(() => import("@/pages/legal/AccessibilityPage"));
const SitemapPage = lazy(() => import("@/pages/legal/SitemapPage"));
const CookiesPage = lazy(() => import("@/pages/legal/CookiesPage"));
const CguPage = lazy(() => import("@/pages/legal/CguPage"));
const ConfidentialitePage = lazy(() => import("@/pages/legal/ConfidentialitePage"));
const ReclamationPage = lazy(() => import("@/pages/legal/ReclamationPage"));

// Espace salarié — parcours unique « Mon parcours retraite »
const ParcoursPage = lazy(() => import("@/pages/app/ParcoursPage"));
const ProfilePage = lazy(() => import("@/pages/app/ProfilePage"));
const NotFoundPage = lazy(() => import("@/pages/app/NotFoundPage"));
const HelpPage = lazy(() => import("@/pages/app/HelpPage"));
const RdvPage = lazy(() => import("@/pages/app/RdvPage"));
const HistoriquePage = lazy(() => import("@/pages/app/HistoriquePage"));
const MessagesPage = lazy(() => import("@/pages/app/MessagesPage"));
const LibraryPage = lazy(() => import("@/pages/app/LibraryPage"));
const SalarieFormationsPage = lazy(() => import("@/pages/app/SalarieFormationsPage"));

// Espace Aon
const AonDashboardPage = lazy(() => import("@/pages/aon/AonDashboardPage"));
const AonDossiersPage = lazy(() => import("@/pages/aon/AonDossiersPage"));
const AonDossierDetailPage = lazy(() => import("@/pages/aon/AonDossierDetailPage"));
const AonDossierFichePage = lazy(() => import("@/pages/aon/AonDossierFichePage"));
const AonDossierParcoursPage = lazy(() => import("@/pages/aon/AonDossierParcoursPage"));
const AonAgendaPage = lazy(() => import("@/pages/aon/AonAgendaPage"));
const AonOperationsPage = lazy(() => import("@/pages/aon/AonOperationsPage"));
const AonEquipePage = lazy(() => import("@/pages/aon/AonEquipePage"));
const AonBibliothequePage = lazy(() => import("@/pages/aon/AonBibliothequePage"));
const AonMessagesPage = lazy(() => import("@/pages/aon/AonMessagesPage"));
const AonRelancesPage = lazy(() => import("@/pages/aon/AonRelancesPage"));
const AonAnomaliesPage = lazy(() => import("@/pages/aon/AonAnomaliesPage"));
const AonFormationsPage = lazy(() => import("@/pages/aon/AonFormationsPage"));
const AonRevuePage = lazy(() => import("@/pages/aon/AonRevuePage"));
const AonPilotagePage = lazy(() => import("@/pages/aon/AonPilotagePage"));
const AonSyntheseDrhPage = lazy(() => import("@/pages/aon/AonSyntheseDrhPage"));

// Espace DRH
const DrhDashboardPage = lazy(() => import("@/pages/drh/DrhDashboardPage"));
const DrhOperationsPage = lazy(() => import("@/pages/drh/DrhOperationsPage"));
const DrhSalariesPage = lazy(() => import("@/pages/drh/DrhSalariesPage"));
const DrhSalarieDetailPage = lazy(() => import("@/pages/drh/DrhSalarieDetailPage"));
const DrhDossierPartagePage = lazy(() => import("@/pages/drh/DrhDossierPartagePage"));
const DrhReportingPage = lazy(() => import("@/pages/drh/DrhReportingPage"));
const DrhSynthesePage = lazy(() => import("@/pages/drh/DrhSynthesePage"));
const DrhMessagesPage = lazy(() => import("@/pages/drh/DrhMessagesPage"));
const DrhRelancesPage = lazy(() => import("@/pages/drh/DrhRelancesPage"));
const DrhFormationsPage = lazy(() => import("@/pages/drh/DrhFormationsPage"));
const DrhLibraryPage = lazy(() => import("@/pages/drh/DrhLibraryPage"));

// Espace Admin
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const AdminOnboardingPage = lazy(() => import("@/pages/admin/AdminOnboardingPage"));
const TenantsPage = lazy(() => import("@/pages/admin/TenantsPage"));
const AdminOperationsPage = lazy(() => import("@/pages/admin/AdminOperationsPage"));
const AdminSalariesPage = lazy(() => import("@/pages/admin/AdminSalariesPage"));
const AdminSalarieDetailPage = lazy(() => import("@/pages/admin/AdminSalarieDetailPage"));
const AdminSalarieParcoursPage = lazy(() => import("@/pages/admin/AdminSalarieParcoursPage"));
const AdminDossierDetailPage = lazy(() => import("@/pages/admin/AdminDossierDetailPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const BrandingPage = lazy(() => import("@/pages/admin/BrandingPage"));
const EmailTemplatesPage = lazy(() => import("@/pages/admin/EmailTemplatesPage"));
const RoadmapPage = lazy(() => import("@/pages/admin/RoadmapPage"));
const AdminRachatBaremePage = lazy(() => import("@/pages/admin/AdminRachatBaremePage"));
const AdminFormationsPage = lazy(() => import("@/pages/admin/AdminFormationsPage"));
const AdminContentLibraryPage = lazy(() => import("@/pages/admin/AdminContentLibraryPage"));
const AdminBriAnnexesPage = lazy(() => import("@/pages/admin/AdminBriAnnexesPage"));
const AuditPage = lazy(() => import("@/pages/admin/AuditPage"));
const RgpdPage = lazy(() => import("@/pages/admin/RgpdPage"));
const AdminControleChecklistPage = lazy(() => import("@/pages/admin/AdminControleChecklistPage"));
const AdminDocumentTypesPage = lazy(() => import("@/pages/admin/AdminDocumentTypesPage"));
const AdminReferentielsPage = lazy(() => import("@/pages/admin/AdminReferentielsPage"));
const AdminConventionsPage = lazy(() => import("@/pages/admin/AdminConventionsPage"));
const AdminReferentielProfessionsPage = lazy(
  () => import("@/pages/admin/AdminReferentielProfessionsPage"),
);
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminAiCostsPage = lazy(() => import("@/pages/admin/AdminAiCostsPage"));
const AdminUsageStatsPage = lazy(() => import("@/pages/admin/AdminUsageStatsPage"));
const AdminExternalServicesPage = lazy(() => import("@/pages/admin/AdminExternalServicesPage"));
const AdminMessagesTrackingPage = lazy(() => import("@/pages/admin/AdminMessagesTrackingPage"));

export default function App() {
  return (
    <BrandingProvider>
      <ImpersonationProvider>
        <AuthProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <BrandingSync />
            <RouteAccessibility />
            <CookieConsent />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                {/* /signup retiré — la création de compte est réservée à l'admin
                  via /admin/users. Toute tentative tombe dans le catch-all global. */}
                <Route path="/signup" element={<Navigate to="/login" replace />} />
                <Route path="/reset" element={<ResetPasswordPage />} />
                <Route path="/auth/confirm" element={<AuthConfirmPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                {/* Double authentification TOTP (A1) : challenge et enrolement,
                    accessibles avec une session aal1, hors shell metier. */}
                <Route path="/auth/mfa" element={<MfaChallengePage />} />
                <Route path="/auth/mfa/enrolement" element={<MfaEnrollPage />} />

                {/* Pages légales publiques (OBS-15) */}
                <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                <Route path="/accessibilite" element={<AccessibilityPage />} />
                <Route
                  path="/audits/accessibilite-2026-10-28.html"
                  element={<Navigate to="/accessibilite" replace />}
                />
                <Route path="/plan-du-site" element={<SitemapPage />} />
                <Route path="/cookies" element={<CookiesPage />} />
                <Route path="/cgu" element={<CguPage />} />
                <Route path="/confidentialite" element={<ConfidentialitePage />} />
                <Route path="/reclamation" element={<ReclamationPage />} />

                {/* Authenticated app shell */}
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <AonPrivacyGate>
                        <AppLayout />
                      </AonPrivacyGate>
                    </ProtectedRoute>
                  }
                >
                  {/* Salarié — parcours unique. Les anciennes pages sont absorbées
                    par le parcours ; leurs routes redirigent vers /app. */}
                  <Route index element={<ParcoursPage />} />
                  <Route path="parcours" element={<ParcoursPage />} />
                  <Route path="historique" element={<HistoriquePage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="dossier" element={<Navigate to="/app" replace />} />
                  <Route path="dossier/wizard" element={<Navigate to="/app" replace />} />
                  <Route path="documents" element={<Navigate to="/app" replace />} />
                  <Route path="simulations" element={<Navigate to="/app" replace />} />
                  <Route path="bri" element={<Navigate to="/app" replace />} />
                  <Route path="messages" element={<MessagesPage />} />
                  <Route path="bibliotheque" element={<LibraryPage />} />
                  <Route path="rdv" element={<RdvPage />} />
                  <Route path="formations" element={<SalarieFormationsPage />} />
                  <Route path="help" element={<HelpPage />} />

                  {/* DRH */}
                  <Route
                    path="drh"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhDashboardPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/operations"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhOperationsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/salaries"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhSalariesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/salaries/:id"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhSalarieDetailPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/dossiers/:id"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhDossierPartagePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/relances"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhRelancesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/formations"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhFormationsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/messages"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhMessagesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/synthese"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhSynthesePage variant="drh" />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/reporting"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhReportingPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="drh/bibliotheque"
                    element={
                      <RoleGuard allow={["drh"]}>
                        <DrhLibraryPage />
                      </RoleGuard>
                    }
                  />

                  {/* Aon production / chef de projet */}
                  <Route
                    path="aon"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonDashboardPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/dossiers"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonDossiersPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/dossiers/fiche/:id"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonDossierFichePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/dossiers/fiche/:id/parcours"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonDossierParcoursPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/dossiers/:id"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonDossierDetailPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/operations"
                    element={
                      <RoleGuard allow={["chef_projet_aon", "admin_aon"]}>
                        <AonOperationsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/equipe"
                    element={
                      <RoleGuard allow={["chef_projet_aon", "admin_aon"]}>
                        <AonEquipePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/calendrier"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonAgendaPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/bibliotheque"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonBibliothequePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/messages"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonMessagesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/relances"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonRelancesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/anomalies"
                    element={
                      <RoleGuard allow={["consultant_aon", "chef_projet_aon", "admin_aon"]}>
                        <AonAnomaliesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/formations"
                    element={
                      <RoleGuard allow={["chef_projet_aon", "admin_aon"]}>
                        <AonFormationsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/revue"
                    element={
                      <RoleGuard allow={["chef_projet_aon", "admin_aon"]}>
                        <AonRevuePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/pilotage"
                    element={
                      <RoleGuard allow={["chef_projet_aon", "admin_aon"]}>
                        <AonPilotagePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="aon/synthese-drh"
                    element={
                      <RoleGuard allow={["chef_projet_aon", "admin_aon"]}>
                        <AonSyntheseDrhPage />
                      </RoleGuard>
                    }
                  />

                  {/* Admin Aon (réservé admin_aon) */}
                  <Route
                    path="admin"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminDashboardPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/onboarding"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminOnboardingPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/tenants"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <TenantsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/operations"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminOperationsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/salaries"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminSalariesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/salaries/:id"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminSalarieDetailPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/salaries/:id/parcours"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminSalarieParcoursPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/dossiers/:id"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminDossierDetailPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/utilisateurs"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <UsersPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/email-templates"
                    element={
                      <RoleGuard allow={["admin_aon", "chef_projet_aon"]}>
                        <EmailTemplatesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/branding"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <BrandingPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/audit"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AuditPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/roadmap"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <RoadmapPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/rachat-bareme"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminRachatBaremePage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/formations"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminFormationsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/bibliotheque"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminContentLibraryPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/annexes-bri"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminBriAnnexesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/rgpd"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <RgpdPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/controle-qualite"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminControleChecklistPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/parametres"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminSettingsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/stats-usage"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminUsageStatsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/services-externes"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminExternalServicesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/couts-ia"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminAiCostsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/suivi-messages"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminMessagesTrackingPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/types-documents"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminDocumentTypesPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/referentiels"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminReferentielsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/conventions-collectives"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminConventionsPage />
                      </RoleGuard>
                    }
                  />
                  <Route
                    path="admin/referentiel-professions"
                    element={
                      <RoleGuard allow={["admin_aon"]}>
                        <AdminReferentielProfessionsPage />
                      </RoleGuard>
                    }
                  />

                  {/* Catch-all dans l'app : page introuvable sobre (A-09) */}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                {/* Catch-all global */}
                <Route path="*" element={<LandingPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </ImpersonationProvider>
    </BrandingProvider>
  );
}
